import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { getRoleHomePath } from '../lib/roleNavigation';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Keycloak configuration
const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL;
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM;
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check for existing auth on mount
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('auth_token');
            const storedUser = localStorage.getItem('user');
            
            if (token && storedUser) {
                try {
                    const response = await authAPI.getMe();
                    setUser(response.data);
                    localStorage.setItem('user', JSON.stringify(response.data));
                } catch (err) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                    setUser(null);
                }
            }
            setLoading(false);
        };

        // Check for Keycloak callback (code in URL) - fallback for redirect mode
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        // Only process if we're on the login page (redirect mode fallback)
        if (code && window.location.pathname === '/login') {
            // Exchange code for token
            handleKeycloakCallback(code);
        } else {
            checkAuth();
        }
    }, []);

    const handleKeycloakCallback = async (code, tenantId = null) => {
        setLoading(true);
        try {
            const redirectUri = window.location.origin + '/keycloak-callback';
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/keycloak/callback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, redirect_uri: redirectUri, tenant_id: tenantId }),
            });

            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('auth_token', data.access_token);
                localStorage.setItem('user', JSON.stringify(data.user));
                setUser(data.user);
                
                // Navigate based on role (no full page refresh needed since we're using popup)
                // Use window.location for clean navigation
                window.location.href = getRoleHomePath(data.user.role);
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Token exchange failed:', errorData.detail || 'Unknown error');
                setError(errorData.detail || 'SSO giriş başarısız');
            }
        } catch (err) {
            console.error('Keycloak callback error:', err);
            setError('SSO bağlantı hatası');
        } finally {
            setLoading(false);
        }
    };

    // Popup window reference
    const [keycloakPopup, setKeycloakPopup] = useState(null);

    // Listen for popup messages
    useEffect(() => {
        const handleMessage = async (event) => {
            // Verify origin
            if (event.origin !== window.location.origin) return;
            
            if (event.data?.type === 'KEYCLOAK_CALLBACK' && event.data?.code) {
                if (keycloakPopup && !keycloakPopup.closed) { keycloakPopup.close(); }
                setKeycloakPopup(null);
                // tenant_id callback'ten gelebilir
                await handleKeycloakCallback(event.data.code, event.data.tenant_id || null);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [keycloakPopup]);

    const loginWithKeycloak = useCallback((tenantConfig = null) => {
        // tenantConfig: { tenant_id, keycloak_url, keycloak_realm, keycloak_client_id }
        // Yoksa global .env kullan
        const kc_url = tenantConfig?.keycloak_url || KEYCLOAK_URL;
        const kc_realm = tenantConfig?.keycloak_realm || KEYCLOAK_REALM;
        const kc_client = tenantConfig?.keycloak_client_id || KEYCLOAK_CLIENT_ID;

        if (!kc_url || !kc_realm) {
            setError('Keycloak yapılandırması eksik');
            return;
        }

        const redirectUri = window.location.origin + '/keycloak-callback';
        const authUrl = `${kc_url}/realms/${kc_realm}/protocol/openid-connect/auth`;

        // state ile tenant_id taşı
        const statePayload = tenantConfig?.tenant_id
            ? JSON.stringify({ tenant_id: tenantConfig.tenant_id, nonce: Math.random().toString(36).slice(2) })
            : undefined;

        const params = new URLSearchParams({
            client_id: kc_client,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: 'openid profile email',
        });
        if (statePayload) params.set('state', btoa(statePayload));

        const popupWidth = 500;
        const popupHeight = 650;
        const left = window.screenX + (window.outerWidth - popupWidth) / 2;
        const top = window.screenY + (window.outerHeight - popupHeight) / 2;

        const popup = window.open(
            `${authUrl}?${params.toString()}`,
            'keycloak-login',
            `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (popup) {
            setKeycloakPopup(popup);
            popup.focus();
            const checkClosed = setInterval(() => {
                if (popup.closed) { clearInterval(checkClosed); setKeycloakPopup(null); }
            }, 500);
        } else {
            console.warn('Popup blocked, falling back to redirect');
            window.location.href = `${authUrl}?${params.toString()}`;
        }
    }, []);

    const login = async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login({ email, password });
            const { access_token, refresh_token, user: userData } = response.data;
            
            localStorage.setItem('auth_token', access_token);
            if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setLoading(false);
            
            return userData;
        } catch (err) {
            // err.response yoksa sunucuya hiç ulaşılamadı (timeout / ağ hatası)
            const message = err.response?.data?.detail
                || (err.response ? 'Giriş başarısız' : 'Sunucuya ulaşılamıyor — lütfen birazdan tekrar deneyin');
            setError(message);
            setLoading(false);
            throw new Error(message);
        }
    };

    const register = async (name, email, password) => {
        setError(null);
        try {
            const response = await authAPI.register({ name, email, password, role: 'user' });
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('auth_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setLoading(false);
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Kayıt başarısız';
            setError(message);
            setLoading(false);
            throw new Error(message);
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (err) {
            // Ignore logout errors
        } finally {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            setUser(null);
        }
    };

    // Global 401 interceptor - auto refresh or logout
    useEffect(() => {
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await originalFetch(...args);
            if (response.status === 401 && user) {
                const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
                if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/refresh')) {
                    // Try refresh token
                    const refreshToken = localStorage.getItem('refresh_token');
                    if (refreshToken) {
                        try {
                            const refreshRes = await originalFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${refreshToken}` },
                            });
                            if (refreshRes.ok) {
                                const data = await refreshRes.json();
                                localStorage.setItem('auth_token', data.access_token);
                                // Retry original request with new token
                                if (args[1]?.headers) {
                                    const newHeaders = new Headers(args[1].headers);
                                    newHeaders.set('Authorization', `Bearer ${data.access_token}`);
                                    args[1].headers = newHeaders;
                                }
                                return originalFetch(...args);
                            }
                        } catch {}
                    }
                    // Refresh failed - logout
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user');
                    setUser(null);
                }
            }
            return response;
        };
        return () => { window.fetch = originalFetch; };
    }, [user]);

    // Role-based access checks. NOT: eski 'admin' rolü kaldırıldı,
    // tenant_admin onun yerini aldı.
    const ADMIN_ROLES = ['super_admin', 'tenant_admin'];
    const REPORT_ROLES = ['super_admin', 'tenant_admin', 'auditor', 'compliance_officer', 'executive', 'manager', 'external_regulator'];
    const AUDIT_ROLES = ['super_admin', 'tenant_admin', 'auditor', 'compliance_officer', 'external_regulator'];
    const AUDITOR_ROLES = ['super_admin', 'tenant_admin', 'auditor', 'compliance_officer', 'external_regulator'];
    // Denetçi panosunu görebilen ama rapor üretme/yazma yapamayan roller.
    const AUDITOR_READ_ONLY_ROLES = ['external_regulator', 'compliance_officer'];
    const READ_ONLY_ROLES = ['external_regulator'];

    const userRole = user?.role || '';
    const isAdmin = ADMIN_ROLES.includes(userRole);
    const isSuperAdmin = userRole === 'super_admin';
    const isComplianceOfficer = userRole === 'compliance_officer';
    const isManager = userRole === 'manager';
    const isAuditor = userRole === 'auditor';
    const isExternalRegulator = userRole === 'external_regulator';
    const isReadOnly = READ_ONLY_ROLES.includes(userRole);

    // Permission checks from backend (with role-based fallbacks)
    const permissions = user?.permissions || {};
    const canAccessAdmin = permissions.can_access_admin ?? isAdmin;
    const canViewReports = permissions.can_view_reports ?? REPORT_ROLES.includes(userRole);
    const canViewAudit = permissions.can_view_audit ?? AUDIT_ROLES.includes(userRole);
    const canAccessAuditor = permissions.can_access_auditor ?? AUDITOR_ROLES.includes(userRole);
    // Denetçi panosuna erişebilir ama rapor üretme/yazma yapamaz (external
    // regulator + compliance officer). UI'da "Rapor Oluştur" gibi yazı
    // butonlarını bu flag ile gizliyoruz.
    const isAuditorReadOnly = AUDITOR_READ_ONLY_ROLES.includes(userRole);
    // External regulator is strictly read-only: block admin/user/content management
    const canManageUsers = permissions.can_manage_users ?? (isAdmin && !isReadOnly);
    const canManageContent = permissions.can_manage_content ?? (isAdmin && !isReadOnly);
    const isDepartmentRestricted = permissions.is_department_restricted ?? isManager;

    const value = {
        user,
        loading,
        error,
        login,
        loginWithKeycloak,
        register,
        logout,
        // Legacy
        isAdmin,
        isAuthenticated: !!user,
        // New role-based
        isSuperAdmin,
        isComplianceOfficer,
        isManager,
        isAuditor,
        isExternalRegulator,
        isReadOnly,
        // Permissions
        canAccessAdmin,
        canViewReports,
        canViewAudit,
        canAccessAuditor,
        isAuditorReadOnly,
        canManageUsers,
        canManageContent,
        isDepartmentRestricted,
        permissions,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
